import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";

const CancellationRefundPolicy: React.FC = () => {
  const policySections = [
    {
      title: `Cancellation & Refund Policy – ${import.meta.env.VITE_COMPANY_NAME}`,
      content: `At ${import.meta.env.VITE_COMPANY_NAME}, operated by ${import.meta.env.VITE_COMPANY_NBFC}, we value your trust and are committed to maintaining a transparent and customer-friendly lending experience. This policy outlines our approach towards loan cancellations and refunds for borrowers using our platform.`,
    },
    {
      title: "1. Pre-Disbursement Cancellation",
      content: `If you change your mind before the loan amount is disbursed, you may cancel your approved application at any time without incurring any additional charges or penalties.`,
    },
    {
      title: "2. Post-Disbursement Cancellation",
      content: `If the loan amount has already been credited to your account, you may still request cancellation within 24 hours of disbursement by returning the entire principal amount to ${import.meta.env.VITE_COMPANY_NAME}. Once repayment confirmation is received, your loan will be marked as cancelled in our system.`,
    },
    {
      title: "3. Refund of Excess Payments",
      content: `Any overpayments made towards your loan account will be reconciled and refunded within 5–7 working days after review. Refunds are processed electronically to the original payment source used for repayment.`,
    },
    {
      title: "4. Processing & Timelines",
      list: [
        "Refunds are initiated only after internal verification and reconciliation.",
        "The timeline for refund reflection depends on your bank or payment gateway policy.",
        "Refund confirmation will be communicated via email/SMS registered with the company.",
      ],
    },
    {
      title: "5. Exceptions",
      list: [
        "Loans once disbursed cannot be cancelled beyond 24 hours of credit.",
        "Interest, processing fees, or service charges are non-refundable once the loan has been activated.",
        "Duplicate transactions or technical payment errors will be refunded after due verification.",
      ],
    },
    {
      title: "6. Contact & Resolution",
      content: `If you wish to raise a cancellation or refund request, please contact our support team at your registered email or through the support channels mentioned on our website. All valid refund or cancellation claims will be processed within the regulatory timelines.`,
    },
    {
      title: "Acknowledgment",
      content: `By availing our services, you acknowledge that you have read, understood, and agreed to this Cancellation & Refund Policy. Thank you for choosing ${import.meta.env.VITE_COMPANY_NAME} — your reliable partner for transparent and responsible financial services.`,
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary font-mono selection:bg-secondary selection:text-white ">
      <SEO
        title="Cancellation & Refund Policy"
        description={`Official cancellation and refund protocols for ${import.meta.env.VITE_COMPANY_NAME}. Learn about pre-disbursement cancellation and excess payment refunds.`}
        keywords={`cancellation policy, refund policy, loan cancellation India, ${import.meta.env.VITE_COMPANY_NAME} refund`}
      />
      <Navbar />

      {/* Official Registry Hero */}
      <section className="w-full bg-text-primary py-32 md:py-48 text-center px-6 relative overflow-hidden border-b-8 border-secondary mb-24">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-4 px-6 py-2 bg-white/5 border border-white/10 text-secondary text-[10px] font-black uppercase tracking-[0.5em] mb-12">
            <div className="w-2 h-2 bg-secondary animate-pulse" />
            Refund_Archive_Protocol_v3.2
          </div>
          <h1 className="text-5xl md:text-[6rem] font-black tracking-tighter leading-[0.85] mb-12 uppercase text-white italic">
            Cancellation <span className="text-secondary not-italic">& Refund.</span>
          </h1>
          <p className="text-white/60 text-lg md:text-2xl max-w-3xl mx-auto font-bold leading-relaxed border-y border-white/10 py-10 uppercase tracking-tight">
            Institutional protocol for transaction reversals. <br />
            <span className="text-white">Learn about our cancellation windows and refund timelines.</span>
          </p>
        </div>
      </section>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pb-40">
        <div className="border-t-4 border-text-primary">
          {policySections.map((section, idx) => (
            <div key={idx} className="border-b border-text-primary/10 py-16 group">
              <div className="flex flex-col gap-10">
                <div className="flex items-start gap-8">
                  <span className="text-secondary font-black text-xs pt-1">SEC_00{idx + 1}</span>
                  <h2 className="text-3xl md:text-5xl font-black text-text-primary leading-tight uppercase tracking-tighter italic">
                    {section.title.split('. ')[1] || section.title}
                  </h2>
                </div>

                <div className="pl-16 md:pl-24 space-y-8">
                  {section.content && (
                    <p className="text-text-secondary font-bold text-lg leading-relaxed uppercase tracking-tight border-l-4 border-text-primary/5 pl-8 italic">
                      {section.content}
                    </p>
                  )}
                  {section.list && (
                    <ul className="space-y-6">
                      {section.list.map((li, i) => (
                        <li key={i} className="flex items-start gap-4 text-text-primary font-bold text-base leading-relaxed">
                          <div className="w-2 h-2 bg-secondary mt-2 shrink-0" />
                          <span className="uppercase tracking-tight">{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CancellationRefundPolicy;
